import { type ReactNode, useEffect, useMemo, useRef } from 'react'

import { type Page, type Router, subscribableCreate, subscribeEventTarget } from '@subaio/utils'

import { type ContextType, context } from './context'
import { getCurrentPage, getLocation } from './utils'

export type Props = {
	children: ReactNode
	router: Router
	page: Page | null
	onPageChange: (page: Page) => void
	/** Optionally transform the page. Useful for passing along global query parameters for instance. */
	transformPage?: (previous: Page | null, next: Page) => Page
}

export const RouterProvider = ({ children, router, page, onPageChange, transformPage }: Props) => {
	const subscribable = useMemo(() => subscribableCreate(), [])

	useEffect(
		() =>
			subscribeEventTarget(window, 'popstate', () => {
				onPageChange(getCurrentPage(router) ?? { name: 'NotFound' })
				subscribable.update()
			}),
		[onPageChange, router, subscribable],
	)

	const pageRef = useRef(page)
	pageRef.current = page

	const methods = useMemo<Omit<ContextType, 'page' | 'url'>>(
		() => ({
			getPath: (targetPage, base) => router.getPath(targetPage, base),
			push: (nextPage) => {
				if (typeof nextPage === 'function') {
					nextPage = nextPage(pageRef.current)
				}
				if (transformPage) {
					nextPage = transformPage(pageRef.current, nextPage)
				}
				const path = router.getPath(nextPage)
				if (path) {
					window.history.pushState(null, '', path)
				}
				onPageChange(nextPage)
				subscribable.update()
			},
			replace: (nextPage) => {
				if (typeof nextPage === 'function') {
					nextPage = nextPage(pageRef.current)
				}
				if (transformPage) {
					nextPage = transformPage(pageRef.current, nextPage)
				}
				const path = router.getPath(nextPage)
				if (path) {
					window.history.replaceState(null, '', path)
				}
				onPageChange(nextPage)
				subscribable.update()
			},
			back: () => {
				window.history.back()
				subscribable.update()
			},
			match: (url) => router.match(url),
			subscribe: subscribable.subscribe,
		}),
		[onPageChange, router, subscribable, transformPage],
	)

	const value = useMemo<ContextType>(
		() => ({
			page,
			url: page ? router.getPath(page) : null,
			...methods,
		}),
		[methods, page, router],
	)

	// Ensure normalized routes (trailing and leading slashes)
	useEffect(() => {
		if (page) {
			const path = router.getPath(page, '/')
			if (path && path !== getLocation()) {
				window.history.replaceState(null, '', path)
			}
		}
	}, [page, router])

	return <context.Provider value={value}>{children}</context.Provider>
}
